import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import { useMembership } from '../../../hooks/MembershipProvider/MembershipProvider';
import styles from './Coupons.module.css';

interface CouponInfo {
  id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  usage_limit?: number;
  used_count: number;
  status: string;
  coupon_type: string;
  valid_from: string;
  valid_until: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  // 사용 내역 정보 추가
  last_used_at?: string;
  total_discount_amount: number;
}

interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  start_index: number;
  end_index: number;
}

interface SearchFilters {
  search: string;
  status: string;
  coupon_type: string;
  created_after: string;
  created_before: string;
}

interface SortCondition {
  field: string;
  order: 'asc' | 'desc';
}

const Coupons: React.FC = () => {
  const { getCoupons, deleteCoupon, getCouponStats, cleanupExpiredCoupons, getHeaders } = useApi();
  const { user, hasAdminPermission, hasSystemPermission } = useMembership();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [coupons, setCoupons] = useState<CouponInfo[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    search: '',
    status: '',
    coupon_type: '',
    created_after: '',
    created_before: ''
  });
  
  const [sortConditions, setSortConditions] = useState<SortCondition[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 쿠폰 목록 조회
  const fetchCoupons = useCallback(async (page: number = 1, filters: SearchFilters = searchFilters, size: number = pageSize, sorts: SortCondition[] = sortConditions) => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page,
        size,
        ...filters
      };
      
      // 정렬 파라미터 추가
      if (sorts.length > 0) {
        params.sort_by = sorts.map(sort => `${sort.field}:${sort.order}`);
      }
      
      // 빈 값 제거
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await getCoupons(params);
      
      if (response) {
        setCoupons(response.coupons || []);
        setPagination({
          current_page: response.page || 1,
          page_size: response.size || 10,
          total_records: response.total || 0,
          total_pages: Math.ceil((response.total || 0) / (response.size || 10)),
          has_next: (response.page || 1) < Math.ceil((response.total || 0) / (response.size || 10)),
          has_previous: (response.page || 1) > 1,
          start_index: ((response.page || 1) - 1) * (response.size || 10) + 1,
          end_index: Math.min((response.page || 1) * (response.size || 10), response.total || 0)
        });
      }
    } catch (err) {
      console.error('쿠폰 목록 조회 실패:', err);
      setError(err instanceof Error ? err.message : '쿠폰 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [getCoupons, searchFilters, pageSize, sortConditions]);

  // 쿠폰 통계 조회
  const fetchStats = useCallback(async () => {
    try {
      const response = await getCouponStats();
      setStats(response);
    } catch (err) {
      console.error('쿠폰 통계 조회 실패:', err);
    }
  }, [getCouponStats]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchCoupons();
    fetchStats();
  }, [fetchCoupons, fetchStats]);

  // 검색 필터 변경 핸들러
  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  // 검색 실행
  const handleSearch = () => {
    fetchCoupons(1, searchFilters);
    setCurrentPage(1);
  };

  // 엑셀 다운로드 핸들러
  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      
      // 현재 검색 조건으로 엑셀 다운로드 요청
      const params = {
        ...searchFilters,
        sort_by: sortConditions.length > 0 ? sortConditions[0].field : 'created_at',
        sort_order: sortConditions.length > 0 ? sortConditions[0].order : 'desc',
        export: 'excel'
      };
      
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value.toString());
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/coupons/export?${queryParams.toString()}`, {
        method: 'GET',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('엑셀 다운로드에 실패했습니다.');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // 파일명 생성 (현재 날짜 포함)
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      link.download = `coupons_${dateStr}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('엑셀 다운로드 실패:', err);
      setError(err instanceof Error ? err.message : '엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 필터 초기화
  const handleResetFilters = () => {
    setSearchFilters({
      search: '',
      status: '',
      coupon_type: '',
      created_after: '',
      created_before: ''
    });
    setCurrentPage(1);
  };

  // 정렬 핸들러
  const handleSort = (field: string) => {
    setSortConditions(prev => {
      const existingSort = prev.find(sort => sort.field === field);
      if (existingSort) {
        // 이미 정렬된 필드인 경우 순서 변경
        return prev.map(sort => 
          sort.field === field 
            ? { ...sort, order: sort.order === 'asc' ? 'desc' : 'asc' }
            : sort
        );
      } else {
        // 새로운 필드 정렬 추가
        return [...prev, { field, order: 'asc' }];
      }
    });
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchCoupons(page, searchFilters, pageSize, sortConditions);
  };

  // 페이지 크기 변경 핸들러
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchCoupons(1, searchFilters, size, sortConditions);
  };

  // 쿠폰 삭제 핸들러
  const handleDeleteCoupon = async (couponId: string) => {
    if (!window.confirm('정말로 이 쿠폰을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteCoupon(couponId);
      alert('쿠폰이 성공적으로 삭제되었습니다.');
      fetchCoupons(currentPage, searchFilters, pageSize, sortConditions);
      fetchStats();
    } catch (err) {
      console.error('쿠폰 삭제 실패:', err);
      alert('쿠폰 삭제에 실패했습니다.');
    }
  };

  // 만료된 쿠폰 정리
  const handleCleanupExpiredCoupons = async () => {
    if (!window.confirm('만료된 쿠폰들을 정리하시겠습니까?')) {
      return;
    }

    try {
      const response = await cleanupExpiredCoupons();
      alert(`만료된 쿠폰 ${response.updated_count}개의 상태가 업데이트되었습니다.`);
      fetchCoupons(currentPage, searchFilters, pageSize, sortConditions);
      fetchStats();
    } catch (err) {
      console.error('만료된 쿠폰 정리 실패:', err);
      alert('만료된 쿠폰 정리에 실패했습니다.');
    }
  };

  // 상태 표시 함수
  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { text: string; className: string } } = {
      'active': { text: '활성', className: styles.statusActive },
      'inactive': { text: '비활성', className: styles.statusInactive },
      'expired': { text: '만료', className: styles.statusExpired },
      'used_up': { text: '소진', className: styles.statusUsedUp }
    };
    
    const statusInfo = statusMap[status] || { text: status, className: styles.statusDefault };
    return <span className={`${styles.statusBadge} ${statusInfo.className}`}>{statusInfo.text}</span>;
  };

  // 할인 타입 표시 함수
  const getDiscountTypeText = (couponType: string, value: number) => {
    switch (couponType) {
      case 'PERCENTAGE':
        return `${value}% 할인`;
      case 'FIXED_AMOUNT':
        return `${value.toLocaleString()}원 할인`;
      case 'GIFT_PLAN':
        return `${value.toLocaleString()}원 상품권`;
      case 'FREE_SHIPPING':
        return '무료 배송';
      default:
        return `${couponType}: ${value}`;
    }
  };

  // 권한 확인
  if (!hasAdminPermission() && !hasSystemPermission()) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지에 접근하려면 관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className="admin-page-title">쿠폰 관리</h1>
        <div className={styles.headerActions}>
          {hasSystemPermission() && (
            <button 
              className={styles.btnSecondary}
              onClick={handleCleanupExpiredCoupons}
            >
              만료된 쿠폰 정리
            </button>
          )}
          <button 
            className={styles.btnPrimary}
            onClick={() => navigate('/admin/coupon/create')}
          >
            <span className={styles.btnIcon}>+</span>
            쿠폰 발행
          </button>
        </div>
      </div>

      {/* 통계 카드 - 시스템 관리자만 표시 */}
      {stats && hasSystemPermission() && (
        <div className={styles.statsContainer}>
          <div className={styles.statCard}>
            <h3>전체 쿠폰</h3>
            <p className={styles.statNumber}>{stats.total_coupons}</p>
          </div>
          <div className={styles.statCard}>
            <h3>활성 쿠폰</h3>
            <p className={styles.statNumber}>{stats.active_coupons}</p>
          </div>
          <div className={styles.statCard}>
            <h3>만료 쿠폰</h3>
            <p className={styles.statNumber}>{stats.expired_coupons}</p>
          </div>
          <div className={styles.statCard}>
            <h3>사용률</h3>
            <p className={styles.statNumber}>{stats.usage_rate?.toFixed(1)}%</p>
          </div>
        </div>
      )}

      {/* 검색 필터 */}
      <div className={styles.filterContainer}>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label>검색어</label>
            <input
              type="text"
              value={searchFilters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="쿠폰 코드, 이름으로 검색"
              className={styles.input}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>상태</label>
            <select
              value={searchFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={styles.select}
            >
              <option value="">전체</option>
              <option value="ACTIVE">활성</option>
              <option value="INACTIVE">비활성</option>
              <option value="EXPIRED">만료</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label>쿠폰 타입</label>
            <select
              value={searchFilters.coupon_type}
              onChange={(e) => handleFilterChange('coupon_type', e.target.value)}
              className={styles.select}
            >
              <option value="">전체</option>
              <option value="PERCENTAGE">퍼센트 할인</option>
              <option value="FIXED_AMOUNT">고정 금액 할인</option>
              <option value="GIFT_PLAN">자서전 플랜 선물</option>
              <option value="FREE_SHIPPING">무료 배송</option>
            </select>
          </div>
        </div>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label>생성일 (시작)</label>
            <input
              type="date"
              value={searchFilters.created_after}
              onChange={(e) => handleFilterChange('created_after', e.target.value)}
              className={styles.input}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>생성일 (종료)</label>
            <input
              type="date"
              value={searchFilters.created_before}
              onChange={(e) => handleFilterChange('created_before', e.target.value)}
              className={styles.input}
            />
          </div>
          <div className={styles.filterActions}>
            <button className={styles.btnPrimary} onClick={handleSearch}>
              검색
            </button>
            <button className={styles.btnSecondary} onClick={handleResetFilters}>
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className={styles.errorContainer}>
          <p>{error}</p>
        </div>
      )}

      {/* 쿠폰 목록 테이블 */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <p>쿠폰 목록을 불러오는 중...</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th onClick={() => handleSort('code')}>쿠폰 코드</th>
                <th onClick={() => handleSort('name')}>쿠폰명</th>
                <th onClick={() => handleSort('discount_type')}>할인</th>
                <th onClick={() => handleSort('status')}>상태</th>
                <th onClick={() => handleSort('coupon_type')}>타입</th>
                <th onClick={() => handleSort('last_used_at')}>사용일</th>
                <th onClick={() => handleSort('valid_until')}>만료일</th>
                <th onClick={() => handleSort('created_at')}>생성일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {coupons.length === 0 ? (
                <tr>
                  <td colSpan={9} className={styles.noData}>
                    쿠폰이 없습니다.
                  </td>
                </tr>
              ) : (
                coupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td>
                      <span className={styles.couponCode}>{coupon.code}</span>
                    </td>
                    <td>
                      <div className={styles.couponName}>
                        <strong>{coupon.name}</strong>
                      </div>
                    </td>
                    <td>
                      <span className={styles.discountValue}>
                        {getDiscountTypeText(coupon.coupon_type, coupon.discount_value)}
                      </span>
                    </td>
                    <td>{getStatusBadge(coupon.status)}</td>
                    <td>
                      <span className={styles.couponType}>{coupon.coupon_type}</span>
                    </td>
                    <td>
                      <span className={styles.createdAt}>
                        {coupon.last_used_at 
                          ? new Date(coupon.last_used_at).toLocaleDateString()
                          : <span className={styles.unusedTag}>미사용</span>
                        }
                      </span>
                    </td>
                    <td>
                      <span className={styles.validUntil}>
                        {new Date(coupon.valid_until).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <span className={styles.createdAt}>
                        {new Date(coupon.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.btnSmall}
                          disabled
                          style={{ opacity: 0.5, cursor: 'not-allowed' }}
                        >
                          상세
                        </button>
                        <button
                          className={`${styles.btnSmall} ${styles.btnDanger}`}
                          onClick={() => handleDeleteCoupon(coupon.id)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 페이지네이션 */}
      {pagination && pagination.total_pages > 1 && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            <span>
              {pagination.start_index}-{pagination.end_index} / {pagination.total_records}개
            </span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className={styles.pageSizeSelect}
            >
              <option value={10}>10개씩</option>
              <option value={20}>20개씩</option>
              <option value={50}>50개씩</option>
            </select>
          </div>
          <div className={styles.paginationButtons}>
            <button
              className={styles.paginationBtn}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.has_previous}
            >
              이전
            </button>
            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              const pageNum = Math.max(1, currentPage - 2) + i;
              if (pageNum > pagination.total_pages) return null;
              return (
                <button
                  key={pageNum}
                  className={`${styles.paginationBtn} ${pageNum === currentPage ? styles.active : ''}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className={styles.paginationBtn}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.has_next}
            >
              다음
            </button>
          </div>
        </div>
      )}

      {/* 엑셀 다운로드 버튼 */}
      <div className={styles.excelDownloadContainer}>
        <button 
          className={styles.btnSecondary}
          onClick={handleDownloadExcel}
          disabled={loading}
        >
          <span className={styles.btnIcon}>
            {loading ? '⟳' : '↓'}
          </span>
          {loading ? '다운로드 중...' : '엑셀 다운로드'}
        </button>
      </div>
    </div>
  );
};

export default Coupons;
